/**
 * Session lifecycle predicates and query helpers.
 *
 * Lifecycle states:
 *   Active     — draft or in-progress.
 *   Completed  — counted and closed; included in historical reports unless voided.
 *   Archived   — moved to Archive page; excluded from the active count dashboard
 *                but historical data is still included in reports (history preserved).
 *   Voided     — completed record marked invalid; excluded from all totals and reports,
 *                visible in Archive page as an audit record only.
 *   Trashed    — soft-deleted; excluded from all views except the Trash page.
 */

export function isVoided(session) {
  return Boolean(session?.voided_at);
}

export function isArchived(session) {
  return Boolean(session?.is_archived);
}

export function isTrashed(session) {
  return Boolean(session?.is_trashed);
}

export function isCompleted(session) {
  return session?.status === 'completed';
}

export function isReportable(session) {
  return isCompleted(session) && !isVoided(session) && !isTrashed(session);
}

/**
 * Apply the voided-session exclusion filter to a Supabase query builder.
 * Voided sessions are audit records only and must never appear in active
 * inventory totals or report calculations.
 *
 * Note: archived sessions are intentionally NOT excluded here — their historical
 * data remains available for reporting (Archive = stop using, not erase history).
 * Trashed sessions are excluded separately by the individual query predicates.
 */
export function applyActiveFilter(query) {
  return query.is('voided_at', null);
}

/**
 * Fetch the current template name and category from count_templates.
 * Returns { name, category } or null when the template no longer exists.
 * Throws on database error so callers surface failures instead of silently
 * completing without a snapshot.
 */
export async function fetchTemplateMeta(supabase, templateId) {
  if (!templateId) return null;
  const { data, error } = await supabase
    .from('count_templates')
    .select('name, category')
    .eq('id', templateId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}
